import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SavedCartFacade } from '@spartacus/cart/saved-cart/root';
import { OccEndpointsService} from '@spartacus/core';
import { combineLatest, Observable } from 'rxjs';
import { withLatestFrom, tap, pluck, shareReplay, distinctUntilChanged, map, take } from 'rxjs/operators';
import { GlobalMessageType, GlobalMessageService, TranslationService} from '@spartacus/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DirectInputForm } from './uig-cart.models';
import { UigExportComponent } from '../uig-export/uig-export.component';
import { UigEmptyCartComponent } from './uig-empty-cart/uig-empty-cart.component';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {isSelectiveCart, MultiCartService} from '@spartacus/cart/base/core';
import {Cart} from '@spartacus/cart/base/root';
import {getWishlistName} from '@spartacus/cart/wish-list/core';
import {LaunchDialogService} from "@spartacus/storefront";


@Injectable({
  providedIn: 'root',
})
export class UigCartService {

  private lastEntryListedStatus: boolean = true;
  private addToCartError: boolean = true;
  private isRemovedEntry: boolean;

  constructor(
    protected httpClient: HttpClient,
    private endpoints: OccEndpointsService,
    private modalService: NgbModal,
    private translation: TranslationService,
    private messageService: GlobalMessageService,
    private launchDialogService: LaunchDialogService,
    protected userAccountFacade: UserAccountFacade,
    protected multiCartService: MultiCartService,
    protected savedCartService: SavedCartFacade
  ) {}

  addFastEntry(fastEntryForm: DirectInputForm, activeCartId: string): Observable<Object> {
    const url = this.endpoints.buildUrl('customAddFastEntry', {queryParams: { activeCartId }});
    return this.httpClient.post(url, fastEntryForm);
  }

  addFromWishlist(productCodes: string, activeCartId: string): Observable<Object> {
    const url = this.endpoints.buildUrl('customAddFromWishlist', {queryParams: { productCodes, activeCartId }});
    return this.httpClient.post(url, null);
  }

  importFile(file: File, activeCartId: string) {
    var formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('activeCartId', activeCartId);
    const url = this.endpoints.buildUrl('ImportProductsUpload');
    return this.httpClient.post(url, formData);
  }

  updateCartEntryNote(entryNumber: number, note: string, activeCartId: string): Observable<Object> {
    const url = this.endpoints.buildUrl('customUpdateCartEntryNote', {urlParams: { entryNumber }, queryParams: { note, activeCartId }});
    return this.httpClient.post(url, null);
  }

  public printOrExportCart(activeCartId: string): void {
    this.modalService.open(UigExportComponent, { centered: true }).result.then(
      (result) => {
        if (result === 'print') {
          var css = '@page { size: auto; }',
          head = document.head || document.getElementsByTagName('head')[0],
          style = document.createElement('style');

          style.media = 'print';
          style.appendChild(document.createTextNode(css));

          head.appendChild(style);
          window.print();
        } else {
          this.exportCart(result, activeCartId);
        }
      },
      (reason) => {}
    );
  }

  exportCart(exportType: string, activeCartId: string) {
    const params = { activeCartId: activeCartId };
    const url = this.endpoints.buildUrl('cartDownload', {queryParams: params});
    this.httpClient
      .post(url, null, { observe: 'body', responseType: 'blob' })
      .pipe(
        tap(
          (data) => {
            this.translation
              .translate('cartExport.cartFilename')
              .pipe(take(1))
              .subscribe((fileName) => {
                var a = document.createElement('a');
                a.setAttribute('download', fileName + '.' + exportType);
                a.href = URL.createObjectURL(data);
                a.click();
              });
          },
          () => {
            this.translation
              .translate('cartExport.errorExportCart')
              .pipe(take(1))
              .subscribe((message) => {
                this.messageService.add(
                  message,
                  GlobalMessageType.MSG_TYPE_ERROR
                );
              });
          }
        )
      )
      .subscribe((res) => {});
  }

  updatePublicCartFlag(publicCart: boolean, activeCartId: string) {
    const url = this.endpoints.buildUrl('updatePublicCartFlag', {queryParams: { publicCart, activeCartId }});
    return this.httpClient.post(url, null);
  }

  getList(): Observable<Cart[]> {
    return this.getSavedCartList().pipe(
      withLatestFrom(this.savedCartService.getSavedCartListProcess()),
      tap(([_, state]) => {
        if (!(state.loading || state.success || state.error)) {
          this.savedCartService.loadSavedCarts();
        }
      }),
      pluck(0),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getSavedCartList(): Observable<Cart[]> {
    return combineLatest([
      this.multiCartService.getCarts(),
      this.userAccountFacade.get(),
    ]).pipe(
      distinctUntilChanged(),
      map(([carts, user]) =>
        carts.filter(
          (cart) =>
            (user?.uid !== undefined
              ? cart?.name !== getWishlistName(user?.uid)
              : true) &&
            !isSelectiveCart(cart?.code) &&
            cart != null
        )
      )
    );
  }

  hasAddToCartError(): boolean {
    return this.addToCartError;
  }

  getLastEntryListedStatus(): boolean {
    return this.lastEntryListedStatus;
  }

  setLastEntryListedStatus(lastEntryListedStatus: boolean): void {
    this.addToCartError = false;
    this.lastEntryListedStatus = lastEntryListedStatus;
  }

  getIsRemovedEntry(): boolean {
    return this.isRemovedEntry;
  }

  setIsRemovedEntry(isRemovedEntry: boolean): void {
    this.isRemovedEntry = isRemovedEntry;
  }

  public openEmptyCartComponent(activeCartId: string): void {
    const dialog = this.launchDialogService.openDialog('EMPTY_CART', undefined, undefined, {activeCartId});
    dialog?.pipe(take(1)).subscribe();
  }

  removeAllEntries(activeCartId: string) {
    const url = this.endpoints.buildUrl('removeAllEntries', {queryParams: { activeCartId }});
    return this.httpClient.delete(url);
  }
}
